/**
 * /redefinir — set a new password from a recovery link.
 *
 * The link can arrive in three shapes depending on the GoTrue flow, so all
 * three are handled:
 *   1. `?code=...`        PKCE (our client sets flowType:'pkce'). supabase-js
 *                         exchanges it automatically because detectSessionInUrl
 *                         is on; we just wait for the session/PASSWORD_RECOVERY.
 *                         NB the PKCE verifier lives in localStorage, so the link
 *                         must be opened in the SAME browser that requested it.
 *   2. `?token_hash=&type=recovery`  verified explicitly via verifyOtp — this one
 *                         works cross-device, so it is worth supporting.
 *   3. `#access_token=...&type=recovery`  legacy implicit flow, also picked up by
 *                         detectSessionInUrl.
 * Expired/used links come back as `#error=...&error_code=otp_expired`.
 */
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { useAuth } from '../contexts/AuthProvider';
import { translateAuthError } from '../lib/authErrors';
import { isSupabaseConfigured, supabase } from '../lib/supabase';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const EMBER_BTN =
  'w-full inline-flex items-center justify-center gap-2 rounded-xl bg-ember px-4 py-3 font-semibold text-ink-950 shadow-[0_0_24px_-10px_#F26A4B] transition-all duration-200 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed';
const ERR_BOX =
  'rounded-xl bg-risk-high/10 border border-risk-high/30 text-risk-high text-sm p-3';
const OK_BOX =
  'rounded-xl bg-tutela-growth/10 border border-tutela-growth/30 text-tutela-growth text-sm p-3';

const MIN_LEN = 8;

type Phase = 'checking' | 'ready' | 'invalid' | 'done';

export default function Redefinir() {
  useDocumentMeta('Redefinir palavra-passe | Tutela', undefined, undefined, { noindex: true });
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('checking');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const handled = useRef(false);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLinkError('Autenticação indisponível nesta configuração.');
      setPhase('invalid');
      return;
    }
    // StrictMode mounts effects twice in dev; a recovery token is single-use.
    if (handled.current) return;
    handled.current = true;

    const url = new URL(window.location.href);
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
    const errCode = url.searchParams.get('error_code') ?? hash.get('error_code');
    const errDesc = url.searchParams.get('error_description') ?? hash.get('error_description');
    const tokenHash = url.searchParams.get('token_hash');
    const type = url.searchParams.get('type') ?? hash.get('type');

    if (errCode) {
      setLinkError(
        /expired/i.test(errCode)
          ? 'Esta ligação expirou. Peça uma nova ligação de recuperação.'
          : (errDesc ?? 'Ligação inválida. Peça uma nova ligação de recuperação.'),
      );
      setPhase('invalid');
      return;
    }

    let cancelled = false;

    // Shape 2: explicit token_hash verification (works across devices).
    if (tokenHash && type === 'recovery') {
      supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' }).then(({ error }) => {
        if (cancelled) return;
        if (error) {
          setLinkError(
            'Ligação inválida ou já utilizada. Peça uma nova ligação de recuperação.',
          );
          setPhase('invalid');
          return;
        }
        setPhase('ready');
      });
      return;
    }

    // Shapes 1 and 3: supabase-js processes the URL itself. Wait for the
    // resulting session (PASSWORD_RECOVERY), with a bounded timeout so a
    // malformed link doesn't leave a spinner forever.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY' || session) setPhase('ready');
    });

    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled && data.session) setPhase('ready');
    });

    const timer = window.setTimeout(() => {
      if (cancelled) return;
      setPhase((p) => {
        if (p !== 'checking') return p;
        setLinkError(
          'Não foi possível validar a ligação. Se a abriu noutro dispositivo ou navegador, ' +
            'peça uma nova ligação e abra-a no mesmo sítio onde fez o pedido.',
        );
        return 'invalid';
      });
    }, 6000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      sub.subscription.unsubscribe();
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_LEN) {
      setError(`A palavra-passe deve ter pelo menos ${MIN_LEN} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setError('As palavras-passe não coincidem.');
      return;
    }
    setSaving(true);
    const { error } = await updatePassword(password);
    setSaving(false);
    if (error) {
      setError(translateAuthError(error));
      return;
    }
    setPhase('done');
  }

  if (phase === 'checking') {
    return (
      <AuthLayout title="Nova palavra-passe">
        <p className="text-sm text-mist-muted">A validar a ligação...</p>
      </AuthLayout>
    );
  }

  if (phase === 'invalid') {
    return (
      <AuthLayout
        title="Ligação inválida"
        footer={
          <Link to="/entrar" className="text-ember hover:underline">
            Voltar a entrar
          </Link>
        }
      >
        <div className="space-y-4">
          <p className={ERR_BOX}>{linkError}</p>
          <Link to="/recuperar" className={EMBER_BTN}>
            Pedir nova ligação
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'done') {
    return (
      <AuthLayout title="Palavra-passe alterada">
        <div className="space-y-4">
          <p className={OK_BOX}>A sua palavra-passe foi alterada com sucesso.</p>
          <button
            type="button"
            onClick={() => navigate('/ignis', { replace: true })}
            className={EMBER_BTN}
          >
            Continuar
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Nova palavra-passe" subtitle="Defina uma nova palavra-passe para a sua conta.">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label-dark" htmlFor="password">
            Nova palavra-passe
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LEN}
            className="input-dark"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
          <p className="mt-1 text-xs text-mist-muted">Mínimo {MIN_LEN} caracteres.</p>
        </div>
        <div>
          <label className="label-dark" htmlFor="confirm">
            Confirmar palavra-passe
          </label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LEN}
            className="input-dark"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
          />
        </div>
        {error && <p className={ERR_BOX}>{error}</p>}
        <button type="submit" disabled={saving} className={EMBER_BTN}>
          {saving ? 'A guardar...' : 'Guardar palavra-passe'}
        </button>
      </form>
    </AuthLayout>
  );
}
